import React, { FC, useEffect, useRef, useState } from "react";
import { Row, Col, Button, Modal, ModalBody } from "reactstrap";
import { useFormContext } from "react-hook-form";
import FormInput from "../form-components/InputComponent";
import FormSelect from "../form-components/SelectComponent";
import { Icon } from "../../components/Component";
import FormDatePicker from "../form-components/DatePicker";
import GeneralLedgerApis from "../../api/master/generalLedger";
import {
  Divider,
  Space,
  Popover,
  Tooltip,
  Radio,
  Checkbox,
  message,
  Modal as AntdModal,
} from "antd";
import DeliveryAddressMaster from "../masters/delivery-address/DeliveryAddressMaster";
import deliveryAddressApis from "../../api/master/deliveryAddress";
import {
  EditOutlined,
  InfoCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import budgetApis from "../../api/master/budget";
import purchaseOrderCreation from "../../api/purchase-orders/pruchaseOrderCreation";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import contractorsApis from "../../api/master/contractors";
import SettingsForm from "./SettingsForm";
import sequenceApis from "../../api/master/sequence";
import PurchaseOrderApprovalDrawer from "./PurchaseOrderApprovalDrawer";
import sitesApis from "../../api/master/sites";
import rolesApi from "../../api/master/roles";
interface SelectOptions {
  label: string;
  value: string;
}
interface SequenceType {
  module: string;
  level: string;
  siteIds: string[];
}

const ADVANCE_MILESTONE_CONFIG: {
  key: string;
  label: string;
  milestoneName: string;
  alwaysMandatory: boolean;
}[] = [
  {
    key: "milestone0",
    label: "Along with the PO",
    milestoneName: "along with the PO",
    alwaysMandatory: true,
  },
  {
    key: "milestone1",
    label: "Before Dispatch",
    milestoneName: "before Dispatch",
    alwaysMandatory: false,
  },
  {
    key: "milestone2",
    label: "On GRN",
    milestoneName: "on GRN",
    alwaysMandatory: false,
  },
  {
    key: "milestone3",
    label: "Post-Installation / Commissioning",
    milestoneName: "post-installation/commissioning",
    alwaysMandatory: false,
  },
];

const PAYMENT_MODE_OPTIONS = [
  { label: "National Electronic Fund Transfer (NEFT)", value: "neft" },
  { label: "Real Time Gross Settlement (RTGS)", value: "rtgs" },
  { label: "Immediate Payment Service (IMPS)", value: "imps" },
  { label: "CHEQUE", value: "cheque" },
  { label: "UPI", value: "upi" },
  { label: "CASH", value: "cash" },
];

const MilestoneTransactionField: FC<{
  milestoneKey: string;
  required: boolean;
  disabled: boolean;
}> = ({ milestoneKey, required, disabled }) => {
  const { watch, setValue: setVal } = useFormContext();
  const mode = watch(`${milestoneKey}_paymentMode`) || "";
  const fieldName = `${milestoneKey}_transactionNumber`;

  if (["neft", "rtgs", "imps"].includes(mode)) {
    return (
      <FormInput
        name={fieldName}
        label="UTR"
        required={required}
        type="text"
        placeholder="Enter UTR"
        pattern={/^[a-zA-Z0-9/-]*$/}
        patternErrorMessage="Only alphanumeric characters, hyphens, and slashes are allowed"
        onInput={(event: React.FormEvent<HTMLInputElement>) => {
          event.currentTarget.value = event.currentTarget.value.replace(
            /[^a-zA-Z0-9/-]/g,
            "",
          );
        }}
        disabled={disabled}
      />
    );
  }
  if (mode === "upi") {
    return (
      <FormInput
        name={fieldName}
        label="Transaction ID"
        required={required}
        type="text"
        placeholder="Enter Transaction ID"
        pattern={/^[a-zA-Z0-9/-]*$/}
        patternErrorMessage="Only alphanumeric characters, hyphens, and slashes are allowed"
        onInput={(event: React.FormEvent<HTMLInputElement>) => {
          event.currentTarget.value = event.currentTarget.value.replace(
            /[^a-zA-Z0-9/-]/g,
            "",
          );
        }}
        disabled={disabled}
      />
    );
  }
  if (mode === "cheque") {
    return (
      <FormInput
        name={fieldName}
        label="Cheque #"
        required={required}
        type="number"
        placeholder="Enter Cheque Number"
        pattern={/^[0-9]*$/}
        patternErrorMessage="Only numbers are allowed"
        onInput={(event: React.FormEvent<HTMLInputElement>) => {
          event.currentTarget.value = event.currentTarget.value.replace(
            /[^0-9]/g,
            "",
          );
        }}
        disabled={disabled}
      />
    );
  }
  // cash or no mode selected yet
  return null;
};

const AdvanceMilestonesSection: FC<{ isRateContractConversion: boolean }> = ({
  isRateContractConversion,
}) => {
  const { watch, setValue: setVal, trigger } = useFormContext();

  // Total advance percentage across ALL milestones; cap is 100%, balance becomes credit days.
  const totalPct = ADVANCE_MILESTONE_CONFIG.reduce((sum, m) => {
    const v = watch(`${m.key}_advancePercentage`);
    return v !== undefined && v !== null && v !== "" && Number(v) > 0
      ? sum + Number(v)
      : sum;
  }, 0);

  useEffect(() => {
    ADVANCE_MILESTONE_CONFIG.forEach((m) => {
      trigger(`${m.key}_advancePercentage`);
    });
  }, [totalPct, trigger]);

  return (
    <div
      className="p-3 mb-2"
      style={{
        border: "1px solid #e5e9f2",
        borderRadius: 6,
        background: "#fafbfd",
      }}
    >
      <h6 className="mb-2">Advance Payment Milestones</h6>
      <p style={{ color: "#8094ae", fontSize: 12, marginBottom: 12 }}>
        "Along with the PO" is mandatory. For every other milestone, filling
        Advance % makes the rest of that milestone's fields mandatory.
      </p>
      {ADVANCE_MILESTONE_CONFIG.map((m) => {
        const pctField = `${m.key}_advancePercentage`;
        const pct = watch(pctField);
        const hasPct =
          pct !== undefined && pct !== null && pct !== "" && Number(pct) > 0;
        const rowRequired = m.alwaysMandatory || hasPct;
        const modeField = `${m.key}_paymentMode`;
        const modeValue = watch(modeField) || "";
        const isCash = modeValue === "cash";
        const showPaymentFields = m.alwaysMandatory;
        return (
          <div
            key={m.key}
            className="p-3 mb-3"
            style={{
              border: "1px dashed #c9d1e0",
              borderRadius: 6,
              background: "#fff",
            }}
          >
            <div className="mb-2" style={{ fontWeight: 600, fontSize: 13 }}>
              {m.label}
              {m.alwaysMandatory && (
                <span className="mandatory" style={{ marginLeft: 4 }}>
                  *
                </span>
              )}
            </div>
            <Row className="gy-3">
              <Col lg="4">
                <FormInput
                  name={pctField}
                  label={
                    showPaymentFields
                      ? "Advance Percentage"
                      : "Milestone Percentage"
                  }
                  required={rowRequired}
                  type="number"
                  placeholder={
                    showPaymentFields
                      ? "Enter Advance Percentage"
                      : "Enter Milestone Percentage"
                  }
                  pattern={/^(100(\.0{1,2})?|[0-9]{1,2}(\.\d{1,2})?)$/}
                  patternErrorMessage="Enter a valid percentage between 0 and 100 (up to 2 decimal places)"
                  disabled={isRateContractConversion}
                  customValidation={{
                    validate: (value: any) => {
                      if (value === undefined || value === null || value === "")
                        return true;
                      const num = Number(value);
                      if (isNaN(num)) return true;
                      const otherSum = ADVANCE_MILESTONE_CONFIG.reduce(
                        (sum, other) => {
                          if (other.key === m.key) return sum;
                          const v = watch(`${other.key}_advancePercentage`);
                          return v !== undefined &&
                            v !== null &&
                            v !== "" &&
                            Number(v) > 0
                            ? sum + Number(v)
                            : sum;
                        },
                        0,
                      );
                      const remaining = 100 - otherSum;
                      if (otherSum + num > 100) {
                        return remaining <= 0
                          ? "Total advance percentage already at 100%. Reduce other milestones to add here."
                          : `Total advance percentage exceeds 100%. Maximum allowed here: ${remaining.toFixed(2)}%`;
                      }
                      return true;
                    },
                  }}
                />
              </Col>
              {showPaymentFields && (
                <Col lg="4">
                  <FormDatePicker
                    name={`${m.key}_paymentDate`}
                    label="Payment Date"
                    required={rowRequired}
                    placeholder="Select Payment Date"
                  />
                </Col>
              )}
              {showPaymentFields && (
                <Col lg="4">
                  <FormSelect
                    name={modeField}
                    label="Payment Mode"
                    options={PAYMENT_MODE_OPTIONS}
                    required={rowRequired}
                    placeholder="Select Payment Mode"
                    onChange={(value: string) => {
                      setVal(modeField, value);
                      setVal(`${m.key}_transactionNumber`, "");
                    }}
                  />
                </Col>
              )}
              {showPaymentFields && !isCash && (
                <Col lg="4">
                  <FormInput
                    name={`${m.key}_bankName`}
                    label="Bank Name"
                    type="text"
                    placeholder="Enter Bank Name"
                    pattern={/^[A-Za-z\s&.'-]+$/}
                    patternErrorMessage="Bank name should contain only letters"
                    onInput={(event: React.FormEvent<HTMLInputElement>) => {
                      event.currentTarget.value =
                        event.currentTarget.value.replace(
                          /[^A-Za-z\s&.'-]/g,
                          "",
                        );
                    }}
                  />
                </Col>
              )}
              {showPaymentFields && !isCash && (
                <Col lg="4">
                  <MilestoneTransactionField
                    milestoneKey={m.key}
                    required={false}
                    disabled={isRateContractConversion}
                  />
                </Col>
              )}
              <Col lg={showPaymentFields ? "4" : "8"}>
                <FormInput
                  name={`${m.key}_remarks`}
                  label="Remarks"
                  required={rowRequired}
                  type="textarea"
                  rows={2}
                  placeholder="Enter Remark"
                />
              </Col>
            </Row>
          </div>
        );
      })}
      {totalPct > 0 && totalPct < 100 && (
        <Row className="gy-3 mt-1">
          <Col lg="4">
            <FormInput
              name="creditDays"
              label="Credit Days"
              required={true}
              type="number"
              placeholder="Enter Credit Days"
              tooltipText="Total advance is less than 100%. Enter credit days for the remaining balance."
              pattern={/^[1-9][0-9]*$/}
              disabled={isRateContractConversion}
            />
          </Col>
        </Row>
      )}
    </div>
  );
};

const PurchaseOrder_GeneralDetails: FC = (props: any) => {
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [sequenceAccess, setSequenceAccess] = useState<SequenceType>();
  const [activeSequences, setActiveSequences] = useState<any[]>([]);
  const [numberingMode, setNumberingMode] = useState<"auto" | "manual">("auto");
  const [showNumberingModal, setShowNumberingModal] = useState(false);
  const [tempNumberingMode, setTempNumberingMode] = useState<"auto" | "manual">(
    "auto",
  );
  const [restartFiscalYear, setRestartFiscalYear] = useState(false);

  const dispatch: any = useDispatch();
  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  const [contractorOptions, setContractorOptions] = useState<SelectOptions[]>(
    [],
  );
  const [supplierOptions, setSupplierOptions] = useState<SelectOptions[]>([]);

  const [sitesOptions, setSitesOptions] = useState<SelectOptions[]>([]);
  const [generalLedgerOptions, setGeneralLedgerOptions] = useState<
    SelectOptions[]
  >([]);
  const [budgetOptions, setBudgetOptions] = useState<SelectOptions[]>([]);
  const [budgetList, setBudgetList] = useState<any>([]);
  const [deliveryAddressList, setDeliveryAddressList] = useState<any>([]);
  const [deliveryAddressOptions, setDeliveryAddressOptions] = useState<
    SelectOptions[]
  >([]);
  const [popUpContent, setPopUpContent] = useState<JSX.Element | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [approvals, setApprovals] = useState({
    open: false,
    data: [],
  });
  const [isStrictApproval, setIsStrictApproval] = useState<boolean>(false);

  const [isFormDataFetched, setIsFormDataFetched] = useState(false);
  const [conversionRemarksModal, setConversionRemarksModal] = useState(false);
  const [conversionRemarks, setConversionRemarks] = useState("");
  const [paymentMode, setPaymentMode] = useState<string>("");
  const AllFormDetails = watch();
  const needByDateType = watch("needByDateType");
  const deliveryAddressId = watch("deliveryAddressId");
  const billingAddressId = watch("billingAddressId");
  const approvalRequired = watch("approvalRequired");
  const workLevelSettingWatch = watch("workLevelSetting");
  const siteIdWatch = watch("siteId");
  const previousSiteIdRef = useRef<string | undefined>();
  const previousWorkLevelSettingRef = useRef<string | undefined>();
  const contractorIdWatch = watch("contractorId");
  const budgetIdWatch = watch("budgetId");
  const budgetAvailableAmountWatch = watch("budgetAvailableAmount");
  const paymentTermsWatch = watch("paymentTerms");
  const convertedFrom = watch("convertedFrom");
  const isRateContractConversion = convertedFrom === "RC";

  const WORK_LEVEL_OPTIONS = [
    { label: "Tower / Block", value: "TOWER_BLOCK" },
    { label: "Villa", value: "VILLA" },
    { label: "Floor", value: "FLOOR" },
    { label: "Flat", value: "FLAT" },
  ];

  const SEGMENT_TO_WORK_LEVEL: Record<string, string> = {
    Tower: "TOWER_BLOCK",
    "Tower/Block": "TOWER_BLOCK",
    "Tower / Block": "TOWER_BLOCK",
    "Tower/ Block": "TOWER_BLOCK",
    Block: "TOWER_BLOCK",
    Villa: "VILLA",
    Floor: "FLOOR",
    Flat: "FLAT",
  };

  const [isWorkLevelDisabled, setIsWorkLevelDisabled] = useState(true);

  // Initialize conversion remarks from form data
  useEffect(() => {
    const formConversionRemarks = watch("conversionRemarks");
    if (formConversionRemarks && !conversionRemarks) {
      setConversionRemarks(formConversionRemarks);
    }
  }, [watch, conversionRemarks]);

  // Initialize payment mode from form data
  useEffect(() => {
    const formPaymentMode = watch("paymentMode");
    if (formPaymentMode) {
      setPaymentMode(formPaymentMode);
    }
  }, [watch]);

  // Reset transaction number when payment mode changes
  useEffect(() => {
    if (paymentMode) {
      const currentTransactionNumber = watch("transactionNumber");
      if (!currentTransactionNumber) {
        setValue("transactionNumber", "");
      }
    }
  }, [paymentMode, setValue, watch]);

  // Track previous approval required value to detect actual changes
  const [prevApprovalRequired, setPrevApprovalRequired] =
    useState(approvalRequired);

  // Update previous value when approval required changes
  useEffect(() => {
    setPrevApprovalRequired(approvalRequired);
  }, [approvalRequired]);

  // Clear conversion remarks only when user changes from false to true
  useEffect(() => {
    if (prevApprovalRequired === "false" && approvalRequired === "true") {
      setConversionRemarks("");
      setValue("conversionRemarks", "");
    }
  }, [approvalRequired, setValue, prevApprovalRequired]);

  // Initialize approvals from form data when navigating back
  const approvalsDataWatch = watch("approvalsData");
  useEffect(() => {
    if (approvalsDataWatch && !approvals.open && approvals.data.length === 0) {
      try {
        const parsedApprovals =
          typeof approvalsDataWatch === "string"
            ? JSON.parse(approvalsDataWatch)
            : approvalsDataWatch;

        if (
          parsedApprovals &&
          parsedApprovals.data &&
          Array.isArray(parsedApprovals.data) &&
          parsedApprovals.data.length > 0
        ) {
          setApprovals({
            open: false,
            data: parsedApprovals.data,
          });
        }
      } catch (error) {
        console.error("Error parsing approvalsData:", error);
      }
    }
  }, [approvalsDataWatch, approvals.open, approvals.data.length]);
  const [settings, setSettings] = useState({
    open: false,
    data: {},
  });
  const openSettingsForm = () => {
    const currentValues = getValues();
    setSettings({
      open: true,
      data: {
        inwardRequired:
          currentValues?.inwardRequired ??
          AllFormDetails?.inwardRequired ??
          null,
        autoPoDoc:
          currentValues?.autoPoDoc ?? AllFormDetails?.autoPoDoc ?? null,
        needByDateType:
          currentValues?.needByDateType ??
          AllFormDetails?.needByDateType ??
          null,
        deliveryAddressType:
          currentValues?.deliveryAddressType ??
          AllFormDetails?.deliveryAddressType ??
          null,
        workLevelSetting:
          currentValues?.workLevelSetting ??
          AllFormDetails?.workLevelSetting ??
          "NONE",
        towerBlockVillaId:
          currentValues?.towerBlockVillaId ??
          AllFormDetails?.towerBlockVillaId ??
          null,
        floorNo: currentValues?.floorNo ?? AllFormDetails?.floorNo ?? null,
        flatId: currentValues?.flatId ?? AllFormDetails?.flatId ?? null,
        locationId:
          currentValues?.locationId ?? AllFormDetails?.locationId ?? null,
        siteId: currentValues?.siteId ?? AllFormDetails?.siteId ?? siteIdWatch,
      },
    });
  };
  const handleSettingsSubmit = (updatedSettings: any) => {
    setSettings({
      open: false,
      data: updatedSettings,
    });
    // Apply updated settings values in main form (RFQGeneralDetails)
    setValue("inwardRequired", updatedSettings.inwardRequired);
    setValue("autoPoDoc", updatedSettings.autoPoDoc);
    setValue("needByDateType", updatedSettings.needByDateType);
    setValue("deliveryAddressType", updatedSettings.deliveryAddressType);
    setValue("workLevelSetting", updatedSettings.workLevelSetting ?? "NONE");
    setValue("towerBlockVillaId", updatedSettings.towerBlockVillaId ?? null);
    setValue("floorNo", updatedSettings.floorNo ?? null);
    setValue("flatId", updatedSettings.flatId ?? null);
    setValue("locationId", updatedSettings.locationId ?? null);
  };

  const handleWorkLevelSettingChange = (value: string) => {
    const resolvedValue = value || null;
    setValue("workLevelSetting", resolvedValue);

    if (!resolvedValue) {
      setValue("towerBlockVillaId", null);
      setValue("floorNo", null);
      setValue("flatId", null);
      setValue("locationId", null);
      return;
    }

    if (resolvedValue === "TOWER_BLOCK" || resolvedValue === "VILLA") {
      setValue("floorNo", null);
      setValue("flatId", null);
    }

    if (resolvedValue === "FLOOR") {
      setValue("flatId", null);
    }
  };
  useEffect(() => {
    if (budgetIdWatch) {
      const budget = budgetList.find(
        (item: any) => item.headerId == budgetIdWatch,
      );
      if (budget) {
        setValue("costCenterId", budget?.costCenter);
        setValue("departmentId", budget?.department);
        // setValue("contractorId", budget?.contractor);
        // Prefer backend-provided available amount; do not fall back to static values
        const avlAmount =
          budget?.budgetAvailableAmount ??
          budget?.avlAmount ??
          budget?.availableAmount ??
          budget?.remainingAmount ??
          0;
        setValue("budgetAvailableAmount", Number(avlAmount) || 0);
      }
    }
  }, [budgetIdWatch, budgetList, setValue]);

  const fetchSiteHierarchy = async (
    siteId: any,
    shouldOverride: boolean = true,
  ) => {
    try {
      const { data, status } =
        await sitesApis.fetchSegmentLevelBySiteId(siteId);
      if (status && data) {
        const workLevelValue = SEGMENT_TO_WORK_LEVEL[data] || null;
        // Only set value if we should override or if current value is null/undefined
        if (shouldOverride || !getValues("workLevelSetting")) {
          setValue("workLevelSetting", workLevelValue);
        }
        // Set segmentLevel in form so MaterialModal can access it
        setValue("segmentLevel", data);
        setIsWorkLevelDisabled(true);
      } else {
        // If no data is returned, show "Site" by clearing the field
        if (shouldOverride || !getValues("workLevelSetting")) {
          setValue("workLevelSetting", null);
        }
        setValue("segmentLevel", null);
        setIsWorkLevelDisabled(true);
      }
    } catch (error) {
      console.log("Error fetching site hierarchy:", error);
      if (shouldOverride || !getValues("workLevelSetting")) {
        setValue("workLevelSetting", null);
      }
      setValue("segmentLevel", null);
      setIsWorkLevelDisabled(true);
    }
  };

  useEffect(() => {
    const previousSiteId = previousSiteIdRef.current;
    const siteChanged =
      previousSiteId !== undefined && previousSiteId !== siteIdWatch;
    if (siteIdWatch) {
      fetchContractorList(siteIdWatch);
      fetchDeliveryAddressBySite(siteIdWatch);

      // Determine if we should override based on site change or initial load
      const isInitialLoad = previousSiteId === undefined;
      const hasExistingWorkLevel = AllFormDetails?.workLevelSetting != null;
      // Override if: site changed OR (initial load without existing work level)
      const shouldOverride =
        siteChanged || (isInitialLoad && !hasExistingWorkLevel);
      fetchSiteHierarchy(siteIdWatch, shouldOverride);

      if (siteChanged) {
        setValue("deliveryAddressId", "");
        setValue("towerBlockVillaId", null);
        setValue("floorNo", null);
        setValue("flatId", null);
        setValue("locationId", null);
      }
    }
    previousSiteIdRef.current = siteIdWatch;
  }, [siteIdWatch, setValue]);

  useEffect(() => {
    if (siteIdWatch && contractorIdWatch) {
      fetchBudgetDetails(siteIdWatch, contractorIdWatch);
    }
  }, [contractorIdWatch, siteIdWatch]);

  const submitForm = async (data: any) => {
    setValue("approvalsData", JSON.stringify(approvals));
    setValue("isStrictApproval", isStrictApproval);
    setValue("numberingMode", numberingMode);
    if (approvalRequired == "true" && approvals?.data?.length === 0) {
      message.error("Please set the approval hierarchy before proceeding.");
      return;
    }
    if (
      approvalRequired == "false" &&
      (AllFormDetails?.prId || AllFormDetails?.quotationId) &&
      (!conversionRemarks || conversionRemarks.trim() === "")
    ) {
      message.error("Please set conversion remarks before proceeding.");
      return;
    }

    // Check if the logged-in user is the final approver for the Purchase Order
    // module. Run this regardless of approvalRequired so downstream budget
    // validation (including overrun/<0 cases) can rely on the flag.
    if (siteIdWatch) {
      try {
        const moduleRes = await rolesApi.findAllModuleConfig();
        const moduleID = moduleRes?.data?.find(
          (x: any) => x.moduleName == "Purchase Order",
        )?.moduleID;
        let isFinal = false;
        if (moduleID) {
          const res = await rolesApi.fetchHierarchyUsersByModuleId(
            moduleID,
            siteIdWatch,
          );
          const msg = (res?.message || "").toString().toLowerCase();
          isFinal = msg.includes("you are the final");
        }
        setValue("isFinalApprover", isFinal);
      } catch (err) {
        console.log("Error checking final approver:", err);
        setValue("isFinalApprover", false);
      }
    } else {
      setValue("isFinalApprover", false);
    }

    props?.next();
  };

  const fetchSupplier = async () => {
    try {
      const res: any =
        await purchaseOrderCreation.fetchBuyerLinkedSupplierListMethod();
      const { data, status } = res;
      if (status) {
        let supplierOptions = data.map((item: any) => ({
          label: item?.sellerOrg?.name,
          value: item?.sellerOrg?.orgId?.toString(),
        }));
        setSupplierOptions(supplierOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchContractorList = async (siteId: any) => {
    try {
      const { data, status } = await contractorsApis.fetchContractorBySiteId({
        id: "" + siteId,
      });
      if (status) {
        let contractorOptions = data.map((item: any) => ({
          label: item?.companyName,
          value: item?.contractorID?.toString(),
        }));
        setContractorOptions(contractorOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchBudgetDetails = async (siteId: any, contractorId?: any) => {
    try {
      const { data, status } = await budgetApis.getBudgetBySite(
        siteId,
        contractorId,
      );

      if (status) {
        let budgetOptions = data.map((item: any) => ({
          label: item.budgetName,
          value: item.headerId,
        }));
        setBudgetOptions(budgetOptions);
        setBudgetList(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchGeneralLedger = async () => {
    try {
      const { data, status } = await GeneralLedgerApis.fetchLedger();
      if (status) {
        let generalLedgerOptions = data.map((item: any) => ({
          label: item.ledgerName,
          value: item.ledgerID.toString(),
        }));
        setGeneralLedgerOptions(generalLedgerOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDeliveryAddress = async () => {
    try {
      const { data, status } = await deliveryAddressApis.fetchDeliveryAddress();
      if (status) {
        let deliveryAddressOptions = data.map((item: any) => ({
          label: item.deliveryLocationName,
          value: item.id.toString(),
        }));
        setDeliveryAddressOptions(deliveryAddressOptions);
        setDeliveryAddressList(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDeliveryAddressBySite = async (siteId: any) => {
    try {
      const { data, status } =
        await deliveryAddressApis.fetchAddressBySite(siteId);

      if (status) {
        let deliveryAddressOptions = data.map((item: any) => ({
          label: item.deliveryLocationName,
          value: item.id.toString(),
        }));
        setDeliveryAddressOptions(deliveryAddressOptions);
        setDeliveryAddressList(data);
      }
    } catch (error) {
      console.log("Error fetching delivery addresses by site:", error);
    }
  };

  useEffect(() => {
    if (siteIdWatch) {
      fetchDeliveryAddressBySite(siteIdWatch);

      // If a delivery address is already selected (e.g. when coming back from
      // Materials), don't override it.
      const existingDeliveryAddressId =
        getValues("deliveryAddressId") || AllFormDetails?.deliveryAddressId;
      if (existingDeliveryAddressId) {
        return;
      }

      if (
        AllFormDetails?.quotationId ||
        AllFormDetails?.prId ||
        AllFormDetails?.type === "draft"
      )
        return;

      // For fresh creation without any pre-selected delivery address, ensure the
      // field starts empty.
      setValue("deliveryAddressId", "");
    }
  }, [siteIdWatch]);

  const setFormValues = () => {
    const {
      autoPoDoc,
      needByDateType,
      deliveryAddressType,
      billingAddressId,
      deliveryAddressId,
      inwardRequired,
      approvalsData,
      workLevelSetting,
      towerBlockVillaId,
      floorNo,
      flatId,
      locationId,
    } = AllFormDetails;
    if (autoPoDoc) {
      setValue("autoPoDoc", autoPoDoc);
    } else {
      setValue("autoPoDoc", "true");
    }
    if (needByDateType) {
      setValue("needByDateType", needByDateType);
    } else {
      setValue("needByDateType", "Order Level");
    }
    if (deliveryAddressType) {
      setValue("deliveryAddressType", deliveryAddressType);
    } else {
      setValue("deliveryAddressType", "Order Level");
    }
    if (inwardRequired) {
      setValue("inwardRequired", inwardRequired);
    } else {
      setValue("inwardRequired", "true");
    }
    if (billingAddressId) {
      setValue("billingAddressId", billingAddressId);
    }
    if (deliveryAddressId) {
      setValue("deliveryAddressId", deliveryAddressId);
    }
    setValue("workLevelSetting", workLevelSetting ?? null);
    setValue("towerBlockVillaId", towerBlockVillaId ?? null);
    setValue("floorNo", floorNo ?? null);
    setValue("flatId", flatId ?? null);
    setValue("locationId", locationId ?? null);
    setSettings((prev) => ({
      ...prev,
      data: {
        needByDateType: needByDateType || "Order Level",
        deliveryAddressType: deliveryAddressType || "Order Level",
        inwardRequired: inwardRequired || "true",
        autoPoDoc: autoPoDoc || "true",
        workLevelSetting: workLevelSetting || "NONE",
        towerBlockVillaId: towerBlockVillaId ?? null,
        floorNo: floorNo ?? null,
        flatId: flatId ?? null,
        locationId: locationId ?? null,
      },
    }));
    if (approvalsData) {
      const _approvals = JSON.parse(approvalsData);
      setApprovals(_approvals);
    }
  };

  useEffect(() => {
    // fetchSequenceAccess();
    fetchSupplier();
    fetchGeneralLedger();
    fetchDeliveryAddress();
  }, []);
  useEffect(() => {
    if (!isFormDataFetched && Object.keys(AllFormDetails).length > 0) {
      setFormValues();
      setIsFormDataFetched(true);
    }
  }, [AllFormDetails]);

  const fetchSequenceAccess = async () => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleName === "Purchase Order" &&
          new Date(item.endDate) > today &&
          item.status === "Active",
      );
      setActiveSequences(_sequenceAccess || []);

      // Check if mode is already persisted in form (e.g. user navigated back from next step)
      const savedMode = getValues("numberingMode");
      if (savedMode) {
        setNumberingMode(savedMode);
        return;
      }

      // Set initial numbering mode based on available sequences
      if (_sequenceAccess && _sequenceAccess.length > 0 && siteIdWatch) {
        // Check if current site has a sequence
        const siteHasSequence = _sequenceAccess.some(
          (seq: any) =>
            seq.siteId?.toString() === siteIdWatch?.toString() ||
            seq.level === "Org",
        );
        setNumberingMode(siteHasSequence ? "auto" : "manual");
      } else {
        setNumberingMode("manual");
      }
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
      setActiveSequences([]);
      setNumberingMode("manual");
    }
  };

  const getCurrentSiteSequence = () => {
    if (!siteIdWatch || activeSequences.length === 0) return null;

    const siteSequence = activeSequences.find(
      (seq) =>
        seq.level === "Site" &&
        seq.siteId?.toString() === siteIdWatch?.toString(),
    );

    if (siteSequence) return siteSequence;

    const orgSequence = activeSequences.find((seq) => seq.level === "Org");

    if (orgSequence) return orgSequence;

    return null;
  };

  const currentSiteSequence = getCurrentSiteSequence();
  const canAuto = !!siteIdWatch && !!currentSiteSequence;

  useEffect(() => {
    const existingValue = getValues("orderId");

    if (numberingMode === "auto" && currentSiteSequence && !existingValue) {
      const { prefix, nextNumber } = currentSiteSequence;
      const poNumber = `${prefix}`;
      setValue("orderId", poNumber);
    }
  }, [numberingMode, currentSiteSequence]);

  const prevSiteRef = useRef();

  useEffect(() => {
    const prevSite = prevSiteRef.current;

    // Only run if site actually changed
    if (prevSite && prevSite !== siteIdWatch) {
      setValue("orderId", "");
      // Clear persisted mode so new site gets fresh evaluation
      setValue("numberingMode", "");
    }

    // Respect persisted mode on remount
    const savedMode = getValues("numberingMode");
    if (savedMode) {
      setNumberingMode(savedMode);
    } else if (!siteIdWatch) {
      setNumberingMode("manual");
    } else {
      const siteSequence = getCurrentSiteSequence();
      setNumberingMode(siteSequence ? "auto" : "manual");
    }

    prevSiteRef.current = siteIdWatch;
  }, [siteIdWatch]);

  // Re-fetch sequences when component mounts and when site changes
  useEffect(() => {
    if (siteIdWatch) {
      fetchSequenceAccess();
    }
  }, [siteIdWatch]);

  useEffect(() => {
    if (currentUser) {
      let sitesOptions = currentUser?.siteAccessibility?.map((item: any) => ({
        label: item.siteName,
        value: item.siteID.toString(),
      }));
      setSitesOptions(sitesOptions);
    }
  }, [currentUser]);

  // Clear hierarchy fields in existing items when workLevelSetting changes
  useEffect(() => {
    const currentWorkLevelSetting = workLevelSettingWatch;
    const previousWorkLevelSetting = previousWorkLevelSettingRef.current;

    // Only clear if workLevelSetting actually changed (not on initial load)
    if (
      previousWorkLevelSetting !== undefined &&
      currentWorkLevelSetting !== previousWorkLevelSetting
    ) {
      const items = getValues("items");
      if (items && Array.isArray(items) && items.length > 0) {
        items.forEach((item: any, index: number) => {
          setValue(`items.${index}.towerBlockVillaId`, null);
          setValue(`items.${index}.floorNo`, null);
          setValue(`items.${index}.flatId`, null);
          setValue(`items.${index}.locationId`, null);
          setValue(`items.${index}.workLevelSetting`, currentWorkLevelSetting);
        });
      }
    }

    // Update the ref to track the current value
    previousWorkLevelSettingRef.current = currentWorkLevelSetting;
  }, [workLevelSettingWatch, setValue, getValues]);

  const handleModelPopUp = (id?: any, type?: string) => {
    const content = (
      <DeliveryAddressMaster
        formAction={type}
        id={id}
        toggleModelPopUp={() => {
          setIsOpen(false);
          fetchDeliveryAddress();
        }}
      />
    );
    setPopUpContent(content);
    setIsOpen(true);
  };

  const ShowAddressPopOver = ({ id, text, options, matchKey }: any) => {
    if (!id) {
      return <></>;
    }

    let address = options?.find((item: any) => item[matchKey] == id);

    if (!address) {
      return <></>;
    }
    return (
      <Popover
        content={
          <ul>
            <li>
              {address?.addressLine1} {address?.addressLine2}
            </li>
            <li>
              {address?.city},{address?.districtName}, {address?.stateName},
              {address?.countryName} - {address?.pincode}
            </li>
          </ul>
        }
        title={address?.deliveryLocationName}
      >
        <a>{text}</a>
      </Popover>
    );
  };

  return (
    <>
      <Row>
        <div
          style={{ justifyContent: "end", display: "flex", marginTop: "-2%" }}
        >
          <Button color="neutral" onClick={openSettingsForm}>
            Settings &nbsp; <SettingOutlined />
          </Button>
        </div>
      </Row>
      <SettingsForm
        open={settings.open}
        onClose={() => {
          setSettings((pre) => {
            return {
              ...pre,
              open: false,
            };
          });
        }}
        prId={AllFormDetails?.prId}
        initialValues={settings.data}
        onSubmit={handleSettingsSubmit}
      />
      <form className="content clearfix" onSubmit={handleSubmit(submitForm)}>
        <Row className="gy-4">
          <Col lg="4">
            <FormSelect
              name="siteId"
              label={`Site`}
              options={sitesOptions}
              required={true}
              placeholder="-Select-"
              disabled={
                AllFormDetails?.quotationId ||
                AllFormDetails?.prId ||
                AllFormDetails?.type === "draft" ||
                AllFormDetails?.convertedFrom === "RC"
                  ? true
                  : false
              }
              tooltipText="Select the site where the materials will be used."
            />
          </Col>

          <Col lg="8">
            <FormInput
              name="description"
              label="PO Description"
              required={true}
              type="text"
              minLength={2}
              maxLength={500}
              placeholder="Enter Description"
              tooltipText="Enter a brief description of the purchase order."
            />
          </Col>
          <Col lg="4">
            <FormSelect
              name="supplierId"
              label={`Supplier/Vendor`}
              options={supplierOptions}
              required={true}
              placeholder="-Select-"
              tooltipText="Select the supplier or vendor for the purchase order."
              disabled={
                AllFormDetails?.quotationId ||
                AllFormDetails?.convertedFrom === "RC"
                  ? true
                  : false
              }
            />
          </Col>
          <span style={{ display: "none" }}>
            <Col lg="4">
              <FormSelect
                name="costCenterId"
                label={`Cost Center`}
                options={[]}
                placeholder="-Select-"
                tooltipText="Select the cost center associated with this purchase order."
              />
            </Col>
            <Col lg="4">
              <FormSelect
                name="departmentId"
                label={`Department`}
                options={[]}
                required={false}
                placeholder="-Select-"
                tooltipText="Select the department responsible for the purchase order."
              />
            </Col>
          </span>
          <Col lg="4">
            <FormSelect
              name="ledgerId"
              label={`General Ledger`}
              options={generalLedgerOptions}
              placeholder="-Select-"
              tooltipText="Select the general ledger account for financial tracking."
            />
          </Col>
          {/* {(sequenceAccess?.level == "Site" &&
            !sequenceAccess?.siteIds?.includes(siteIdWatch)) ||
            sequenceAccess?.module == "" ? (
            <Col lg="4">
              <FormInput
                name="orderId"
                label={`PO Number`}
                required={true}
                disabled={AllFormDetails?.type === "draft" ? true : false}
                placeholder="Enter PO Number"
              />
            </Col>
          ) : (
            <></>
          )} */}

          <Col lg="4">
            <div style={{ position: "relative" }}>
              <FormInput
                name="orderId"
                label="PO Number"
                required={numberingMode === "manual"}
                disabled={
                  numberingMode === "auto" || AllFormDetails?.type === "draft"
                }
                placeholder={
                  numberingMode === "auto"
                    ? "Auto-generated"
                    : "Enter PO Number"
                }
              />
              <Tooltip title="Click here to enable or disable auto-generation of PO numbers.">
                <SettingOutlined
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50px",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    fontSize: "18px",
                    color: "#1890ff",
                    zIndex: 1,
                  }}
                  onClick={() => {
                    setTempNumberingMode(numberingMode);
                    setShowNumberingModal(true);
                  }}
                />
              </Tooltip>
            </div>
          </Col>

          <Col lg="4">
            <FormSelect
              name="contractorId"
              label={`Contractor`}
              required={true}
              options={contractorOptions}
              placeholder="-Select-"
              tooltipText="Select the contractor for this purchase order."
            />
          </Col>
          <Col lg="4">
            <FormSelect
              name="budgetId"
              label={
                <span>
                  Budget
                  {budgetIdWatch && budgetAvailableAmountWatch >= 0 &&(
                    <span
                      style={{
                        color: "#52c41a",
                        marginLeft: "8px",
                        fontWeight: "normal",
                      }}
                    >
                      {`(Avl Amount: ${Number(
                        budgetAvailableAmountWatch || 0,
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })})`}
                    </span>
                  )}
                  {budgetIdWatch && Number(budgetAvailableAmountWatch) < 0 &&(
                    <span
                      style={{
                        color: "#df1414",
                        marginLeft: "8px",
                        fontWeight: "normal",
                      }}
                    >
                      {`(Overrun By: ${Math.abs(Number(
                        budgetAvailableAmountWatch || 0,
                      )).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })})`}
                    </span>
                  )}
                </span>
              }
              options={budgetOptions}
              required={true}
              placeholder={
                !contractorIdWatch ? "Select a contractor first" : "-Select-"
              }
              disabled={!contractorIdWatch}
              tooltipText="Select the budget for this purchase order."
            />
          </Col>
          <Col lg="4">
            <FormSelect
              name="approvalRequired"
              label="Approval Required ?"
              options={[
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
              ]}
              placeholder="Select"
              disabled={!siteIdWatch}
              required={true}
            />
            <div style={{ display: "none" }}>
              <FormInput
                name="approvalsData"
                label=""
                type="text"
                noMaxLength
              />
            </div>
            {approvalRequired == "true" && (
              <Popover
                content="Click here to set required approvals"
                title="Approval Required"
              >
                <a
                  href="#"
                  className="success"
                  onClick={(e) => {
                    e.preventDefault();
                    setApprovals((pre) => {
                      return {
                        ...pre,
                        open: true,
                      };
                    });
                  }}
                >
                  <EditOutlined /> Set Approvals &nbsp;
                  {approvals?.data?.length == 0 ? (
                    <span className="mandatory">Please set approvals</span>
                  ) : (
                    <></>
                  )}
                </a>
              </Popover>
            )}
            {approvalRequired == "false" &&
              (AllFormDetails?.prId || AllFormDetails?.quotationId) && (
                <div>
                  <div
                    style={{
                      marginTop: "5px",
                      color: "#2263b3",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                    onClick={() => setConversionRemarksModal(true)}
                  >
                    <EditOutlined /> Set Remarks &nbsp;
                    {!conversionRemarks && (
                      <span className="mandatory">Please set remarks</span>
                    )}
                  </div>
                </div>
              )}
          </Col>

          {needByDateType === "Order Level" && (
            <Col lg="4">
              <FormDatePicker
                name="needByDate"
                label="Need By Date"
                required={true}
                minDate={new Date()}
              />
            </Col>
          )}
          <Col lg="4">
            <FormSelect
              name="billingAddressId"
              label={`Billing Address`}
              options={deliveryAddressOptions}
              placeholder={siteIdWatch ? "-Select-" : "Select a site first"}
              disabled={!siteIdWatch}
              popOverOptions={
                <ShowAddressPopOver
                  id={billingAddressId}
                  matchKey="id"
                  options={deliveryAddressList}
                  text="View Selected Billing Address"
                />
              }
              required={true}
              tooltipText="Select the billing address for the purchase order."
            />
          </Col>
          {watch("deliveryAddressType") === "Order Level" && (
            <Col lg="4">
              <FormSelect
                name="deliveryAddressId"
                label="Delivery Address"
                options={deliveryAddressOptions}
                required={true}
                placeholder={siteIdWatch ? "-Select-" : "Select a site first"}
                disabled={
                  AllFormDetails?.quotationId || AllFormDetails?.prId
                    ? true
                    : false
                }
                tooltipText="Select the delivery address for the purchase order."
                popOverOptions={
                  <ShowAddressPopOver
                    id={deliveryAddressId}
                    matchKey="id"
                    options={deliveryAddressList}
                    text="View Selected Delivery Address"
                  />
                }
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: "8px 0" }} />
                    <Space
                      style={{
                        padding: "0 8px 4px",
                        width: "100%",
                        display: "flex",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Button
                        type="button"
                        color="primary"
                        size="small"
                        onClick={() => {
                          handleModelPopUp(null, "");
                        }}
                        style={{
                          width: "100%",
                          textAlign: "center",
                        }}
                        disabled={!siteIdWatch}
                      >
                        <span>Add Address</span>
                      </Button>
                    </Space>
                  </>
                )}
              />
            </Col>
          )}
          <Col lg="4">
            <FormSelect
              name="paymentTerms"
              label="Payment Terms"
              options={[
                { label: "Cash & Carry", value: "Cash and Carry" },
                { label: "Advance", value: "Advance" },
                { label: "Credit", value: "Credit" },
              ]}
              required={true}
              placeholder="Select Payment Terms"
              tooltipText="Select the payment terms for the purchase order."
              disabled={isRateContractConversion}
            />
          </Col>
          {watch("paymentTerms") === "Advance" && (
            <Col lg="12">
              <AdvanceMilestonesSection
                isRateContractConversion={isRateContractConversion}
              />
            </Col>
          )}
          {watch("paymentTerms") === "Credit" && (
            <Col lg="4">
              <FormInput
                name="creditDays"
                label="Credit Days"
                required={true}
                type="number"
                placeholder="Enter Credit Days"
                tooltipText="Enter the credit days for the purchase order."
                pattern={/^[1-9][0-9]*$/}
                disabled={isRateContractConversion}
              />
            </Col>
          )}
          <Col lg="4" style={{ display: "none" }}>
            <FormSelect
              name="autoRelease"
              label="Auto Release ?"
              options={[
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
              ]}
              // required={true}
              placeholder="-Select-"
              tooltipText="If you select Yes then, PO will be sent to the suppliers directly after approvals. If you select no then, PO will come back to the creator and the creator should release it manually to the supplier."
            />
          </Col>
        </Row>
        <Row className="gy-4 mt-1">
          <Col>
            <Button color="primary" type="submit">
              Next
            </Button>
          </Col>
        </Row>
      </form>
      {approvals.open && (
        <PurchaseOrderApprovalDrawer
          open={approvals.open}
          siteId={siteIdWatch}
          onClose={() => {
            setApprovals((pre) => {
              return {
                ...pre,
                open: false,
              };
            });
          }}
          onSubmit={(data: any, isStrictApprovalFromDrawer: boolean) => {
            setApprovals({
              open: false,
              data: data,
            });
            setIsStrictApproval(isStrictApprovalFromDrawer);
          }}
          initialStrictApproval={isStrictApproval}
          setApprovalSubmissionStatus={(status: boolean) => {}}
          approvalData={approvals.data} // Pass the current approval data
        />
      )}
      <Modal
        isOpen={isOpen}
        toggle={() => setIsOpen(false)}
        className="modal-dialog-centered"
        size="xl"
        backdrop="static"
      >
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            setIsOpen(false);
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>

        <ModalBody>{popUpContent}</ModalBody>
      </Modal>

      {/* Conversion Remarks Modal */}
      <Modal
        isOpen={conversionRemarksModal}
        toggle={() => setConversionRemarksModal(false)}
        className="modal-dialog-centered"
        size="md"
        backdrop="static"
      >
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            setConversionRemarksModal(false);
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>
        <ModalBody>
          <h5 className="title">Remarks</h5>
          <div style={{ marginTop: "15px" }}>
            <div className="form-group">
              <div className="form-control-wrap">
                <textarea
                  id="conversionRemarksInput"
                  placeholder="Enter reason for proceeding without approval"
                  className="form-control"
                  value={conversionRemarks}
                  onChange={(e: any) => setConversionRemarks(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Button
              color="primary"
              disabled={!conversionRemarks.trim()}
              onClick={() => {
                setValue("conversionRemarks", conversionRemarks);
                setConversionRemarksModal(false);
              }}
              className="me-2"
            >
              Save
            </Button>
            <Button
              color="secondary"
              onClick={() => setConversionRemarksModal(false)}
            >
              Cancel
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* PO Number Configuration Modal */}
      <AntdModal
        title="Configure PO Number Preference"
        open={showNumberingModal}
        onCancel={() => setShowNumberingModal(false)}
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              gap: "10px",
            }}
          >
            <Button
              key="save"
              color="primary"
              onClick={() => {
                if (tempNumberingMode === "auto" && !currentSiteSequence) {
                  message.error(
                    "Auto-generation is not configured for this site.",
                  );
                  return;
                }

                setNumberingMode(tempNumberingMode);
                setValue("numberingMode", tempNumberingMode);

                if (tempNumberingMode === "auto" && currentSiteSequence) {
                  const { prefix } = currentSiteSequence;
                  const poNumber = `${prefix}`;
                  setValue("orderId", poNumber);
                } else if (tempNumberingMode === "manual") {
                  setValue("orderId", "");
                }
                setShowNumberingModal(false);
              }}
            >
              Save
            </Button>

            <Button
              key="cancel"
              color="primary"
              onClick={() => setShowNumberingModal(false)}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <div style={{ marginBottom: 20, lineHeight: "22px" }}>
          Your purchase order numbers are currently set to auto-generate to save
          time.
          <br />
          Are you sure you want to change this setting?
        </div>

        {!siteIdWatch && (
          <div style={{ color: "#faad14", marginBottom: 16 }}>
            <InfoCircleOutlined /> Sequence configuration is not set for this
            site. Please configure the sequence.
          </div>
        )}

        {siteIdWatch && !currentSiteSequence && (
          <div style={{ color: "#faad14", marginBottom: 16 }}>
            <InfoCircleOutlined /> No sequence configured for the selected site.
            Please set a sequence for this site.
          </div>
        )}

        <Radio.Group
          onChange={(e) => setTempNumberingMode(e.target.value)}
          value={tempNumberingMode}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Radio value="auto" disabled={!canAuto}>
              <span style={{ fontWeight: 500 }}>
                Continue auto-generating PO numbers
              </span>
            </Radio>

            <Radio value="manual">
              <span style={{ fontWeight: 500 }}>Enter PO numbers manually</span>
            </Radio>
          </Space>
        </Radio.Group>
      </AntdModal>
    </>
  );
};

export default PurchaseOrder_GeneralDetails;
